/**
 * Video Card Component
 */

import { api } from '../lib/api.js';
import { getCurrentLanguage, t } from '../lib/i18n.js';

export function createVideoCard(video) {
  const card = document.createElement('div');
  card.className = 'video-card';

  // Use videoId or id (API returns videoId)
  const videoId = video.videoId || video.id;
  card.setAttribute('data-link', '');
  card.setAttribute('href', `/watch/${videoId}`);

  const isFaroese = video.metadata?.isFaroese || video.isFaroese;
  const category = video.metadata?.category || video.category;
  const ageRating = video.ageRating || video.metadata?.ageRating;

  // Get title based on current language
  const currentLang = getCurrentLanguage();
  const originalTitle = video.metadata?.originalTitle || video.originalTitle;
  const internationalTitle = video.metadata?.internationalTitle || video.internationalTitle;
  const title = currentLang === 'fo' ? originalTitle || internationalTitle : internationalTitle || originalTitle;

  // Get poster or still image. The API returns these as path-relative
  // /images/asset/* URLs; resolveAsset prepends the API base so the browser
  // doesn't try to load them from the page origin (which has no such route).
  const rawPosterUrl = video.metadata?.posterUrl || video.posterUrl;
  const posterUrl = api.resolveImageAsset(rawPosterUrl, { w: 640, fit: 'inside', q: 80, fmt: 'webp' });
  const posterSrcSet = api.isAssetProxyUrl(rawPosterUrl)
    ? [
        `${api.resolveImageAsset(rawPosterUrl, { w: 320, fit: 'inside', q: 80, fmt: 'webp' }, { override: true })} 320w`,
        `${api.resolveImageAsset(rawPosterUrl, { w: 640, fit: 'inside', q: 80, fmt: 'webp' }, { override: true })} 640w`,
        `${api.resolveImageAsset(rawPosterUrl, { w: 960, fit: 'inside', q: 80, fmt: 'webp' }, { override: true })} 960w`,
      ].join(', ')
    : '';
  const stillUrl = api.resolveAsset(video.metadata?.stillUrl || video.stillUrl);

  // Get translated category name (singular form for individual film labels)
  const categoryName = category ? t(`category_singular_${category}`) : '';

  // Get year from productionYear or year field
  const year = video.metadata?.productionYear || video.productionYear || video.year;

  // Check if we need to use placeholder with title
  const usePlaceholder = !posterUrl;

  card.innerHTML = `
    <div class="video-card-thumbnail ${usePlaceholder ? 'video-card-placeholder' : ''}">
      ${
        usePlaceholder
          ? `
        ${stillUrl ? `<div class="placeholder-background" style="background-image: url('${stillUrl}');"></div>` : '<div class="placeholder-background" style="background-color: #000;"></div>'}
        <div class="placeholder-title">${title || 'Untitled'}</div>
      `
          : `<img src="${posterUrl}" ${posterSrcSet ? `srcset="${posterSrcSet}" sizes="(max-width: 640px) 45vw, (max-width: 1100px) 30vw, 220px"` : ''} alt="${title || 'Video'}" loading="lazy">`
      }
      <div class="video-card-overlay">
        ${isFaroese ? '<span class="badge badge-original">Faroese Original</span>' : ''}
      </div>
      ${ageRating ? `<span class="age-badge age-badge--${ageRating}" title="${{ A: 'Hóskandi til øll', 7: 'Frámælt børnum undir 7 ár', 11: 'Hóskandi til børn 11 ár og eldri', 15: 'Hóskandi til børn 15 ár og eldri' }[ageRating] || ''}" style="position: absolute; bottom: 8px; left: 8px; display: inline-block; padding: 2px 7px; border-radius: 10px; font-size: 0.7rem; font-weight: 700; line-height: 1.4; color: #fff; z-index: 2; cursor: help; background: ${ageRating === 'A' ? '#2ea043' : ageRating === '7' ? '#d4a72c' : ageRating === '11' ? '#d18616' : '#da3633'};">${ageRating === 'A' ? 'A' : `${ageRating}+`}</span>` : ''}
    </div>
    <div class="video-card-content">
      <h3 class="video-card-title">${title || 'Untitled'}</h3>
      ${categoryName ? `<p class="video-card-category">${categoryName}</p>` : ''}
      <p class="video-card-meta">
        ${video.duration ? formatDuration(video.duration) : ''} 
        ${year ? `${year}` : ''}
      </p>
    </div>
  `;

  return card;
}

function formatDuration(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
